/**
 * Hook: Display unit (XMR / piconero / fiat) with persistent preference.
 * Stores the unit and optionally the selected fiat currency code.
 */

import { useState, useEffect, useCallback } from 'react';
import { getDisplayUnit, setDisplayUnit as persistUnit } from '@/lib/storage/wallet-store';
import { SUPPORTED_FIAT, DEFAULT_FIAT } from '@/lib/monero/fiat';

export type DisplayUnit = 'xmr' | 'piconero' | `fiat:${string}`;

export function useDisplayUnit() {
  const [unit, setUnit] = useState<DisplayUnit>('xmr');
  const [fiatCode, setFiatCode] = useState<string>(DEFAULT_FIAT);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getDisplayUnit().then((stored) => {
      // stored could be 'xmr', 'piconero', or a fiat code like 'USD'
      if (stored === 'xmr' || stored === 'piconero') {
        setUnit(stored);
      } else if (SUPPORTED_FIAT.some(f => f.code === stored)) {
        setUnit(`fiat:${stored}`);
        setFiatCode(stored);
      } else {
        setUnit('xmr');
      }
      setLoaded(true);
    });
  }, []);

  const setAndPersist = useCallback((newUnit: DisplayUnit) => {
    setUnit(newUnit);
    // Persist the unit type: for fiat, store just the currency code
    const persistValue = newUnit.startsWith('fiat:') ? newUnit.slice(5) : newUnit;
    persistUnit(persistValue as 'xmr' | 'piconero');
  }, []);

  const toggle = useCallback(() => {
    if (unit === 'xmr') setAndPersist('piconero');
    else if (unit.startsWith('fiat:')) setAndPersist('xmr');
    else setAndPersist(`fiat:${fiatCode}`); // piconero → fiat
  }, [unit, fiatCode, setAndPersist]);

  const setFiat = useCallback((code: string) => {
    setFiatCode(code);
  }, []);

  return {
    unit,
    fiatCode,
    toggle,
    setUnit: setAndPersist,
    setFiat,
    loaded,
    /** Whether the current unit is a fiat currency */
    isFiat: unit.startsWith('fiat:'),
    /** The fiat code if current unit is fiat, otherwise null */
    activeFiatCode: unit.startsWith('fiat:') ? unit.slice(5) : null,
  };
}
